import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import { useMediaQuery } from 'react-responsive'

import styles from './pagination.module.scss'
import { constants } from '@/lib/constants'

export const Pagination = ({ resultTotal, pageSize, currentPage, gotoPage }) => {
  const getNumberOfPages = (pageSize, resultTotal) => Math.ceil(resultTotal / pageSize)
  const isPreviousButtonDisabled = (currentPage, totalPages) =>
    currentPage === 1 || totalPages === 0
  const isNextButtonDisabled = (currentPage, totalPages) =>
    currentPage === totalPages || totalPages === 1 || totalPages === 0
  const pageUrl = useRouter().asPath.split('?')[0]
  const totalPages = getNumberOfPages(pageSize, resultTotal)
  const isMobile = useMediaQuery({ maxWidth: 991 })
  const numberOfPagesDesktop = 5
  const numberOfPagesMobile = 3
  const maxPagesToDisplay = isMobile ? numberOfPagesMobile : numberOfPagesDesktop

  const handleChange = page => {
    if (gotoPage) {
      gotoPage(page)
    }
  }

  const getPagesToDisplay = (currentPage, totalPages) => {
    const pages = []
    const numberOfPagesToDisplay = maxPagesToDisplay > totalPages ? totalPages : maxPagesToDisplay

    if (currentPage === 1 || currentPage === 2 || currentPage === 3) {
      // currentPage is at the minimum
      for (let i = 1; i <= numberOfPagesToDisplay; i++) {
        pages.push(i)
      }
    } else if (currentPage + 2 < totalPages && currentPage - 2 > 1) {
      // currentPage is in the middle
      for (let i = currentPage - 2; i <= currentPage + 2; i++) {
        pages.push(i)
      }
    } else if (
      currentPage === totalPages ||
      currentPage === totalPages - 1 ||
      currentPage === totalPages - 2
    ) {
      // currentPage is up at the maximum
      for (
        let i = totalPages - numberOfPagesToDisplay === 0 ? 1 : totalPages - numberOfPagesToDisplay;
        i <= totalPages;
        i++
      ) {
        pages.push(i)
      }
    }

    return pages
  }

  const pages = getPagesToDisplay(currentPage, totalPages).map(pageId => (
    <li
      className={`${styles['page-item']} ${pageId === currentPage ? styles.active : ''}`}
      key={pageId}>
      <Link href={`${pageUrl}?page=${pageId}`}>
        <a
          className={styles['page-link']}
          onClick={() => handleChange(pageId)}>
          <span className="sr-only">Page </span>
          {pageId}
        </a>
      </Link>
    </li>
  ))

  return (
    <nav aria-label="Page navigation">
      <ul className={styles.pagination}>
        <li
          className={`${styles['page-item']} ${
            isPreviousButtonDisabled(currentPage, totalPages) ? styles.disabled : ''
          }`}>
          <Link href={`${pageUrl}?page=1`}>
            <a
              className={styles['page-link']}
              onClick={() => handleChange(1)}
              aria-label="First">
              <span aria-hidden="true">
                <Image
                  src={`${constants.imageHost.assets}/icons/library/chevrons-left.svg`}
                  alt=""
                  height="16"
                  width="16"
                />
              </span>
            </a>
          </Link>
        </li>
        <li
          className={`${styles['page-item']} ${
            isPreviousButtonDisabled(currentPage, totalPages) ? styles.disabled : ''
          }`}>
          <Link href={`${pageUrl}?page=${currentPage - 1}`}>
            <a
              className={styles['page-link']}
              onClick={() => handleChange(currentPage - 1)}
              aria-label="Previous">
              <span aria-hidden="true">
                <Image
                  src={`${constants.imageHost.assets}/icons/library/chevron-left.svg`}
                  alt="chevron left icon"
                  height="16"
                  width="16"
                />
              </span>
            </a>
          </Link>
        </li>
        {pages}
        <li
          className={`${styles['page-item']} ${
            isNextButtonDisabled(currentPage, totalPages) ? styles.disabled : ''
          }`}>
          <Link href={`${pageUrl}?page=${currentPage + 1}`}>
            <a
              className={styles['page-link']}
              onClick={() => handleChange(currentPage + 1)}
              aria-label="Next">
              <span aria-hidden="true">
                <Image
                  src={`${constants.imageHost.assets}/icons/library/chevron-right.svg`}
                  alt="chevron right icon"
                  height="16"
                  width="16"
                />
              </span>
            </a>
          </Link>
        </li>
        <li
          className={`${styles['page-item']} ${
            isNextButtonDisabled(currentPage, totalPages) ? styles.disabled : ''
          }`}>
          <Link href={`${pageUrl}?page=${totalPages}`}>
            <a
              className={styles['page-link']}
              onClick={() => handleChange(totalPages)}
              aria-label="Last">
              <Image
                src={`${constants.imageHost.assets}/icons/library/chevrons-right.svg`}
                alt=""
                height="16"
                width="16"
              />
            </a>
          </Link>
        </li>
      </ul>
    </nav>
  )
}

Pagination.propTypes = {
  resultTotal: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  gotoPage: PropTypes.func.isRequired,
}
